import {SpoofedCommitWarning} from '@github-ui/code-view-shared/components/SpoofedCommitWarning'
import {useCodeViewBanners} from '@github-ui/code-view-shared/contexts/CodeViewBannersContext'
import {CurrentTreeProvider, useCurrentTree} from '@github-ui/code-view-shared/contexts/CurrentTreeContext'
import {
  type BlobPayload,
  type DataRouterFilePagePayload,
  isBlobPayload,
  isTreePayload,
  type TreePayload,
} from '@github-ui/code-view-types'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Flash} from '@primer/react'

import {CurrentBlobProvider} from '../hooks/CurrentBlob'
import {forceAnnouncementToScreenReaders} from '../utilities/lines'
import {BlameBannersLegacy} from './blob/Banners/BlameBanners'
import BlobLowerBanners from './blob/Banners/BlobLowerBanners'
import BlobMidBanners from './blob/Banners/BlobMidBanners'
import BlobTopBanners from './blob/Banners/BlobTopBanners'

export default function CodeViewBanners({payload}: {payload: DataRouterFilePagePayload}) {
  if (isTreePayload(payload)) {
    return <TreeBannersLegacy tree={payload.tree} />
  } else if (isBlobPayload(payload)) {
    return <BlobBannersLegacy blob={payload.blob} payload={payload} />
  }

  return null
}

function TreeBannersLegacy({tree}: {tree: TreePayload}) {
  return (
    <CurrentTreeProvider payload={tree}>
      <TreeBanners />
    </CurrentTreeProvider>
  )
}

export function TreeBanners() {
  return (
    <>
      <SpoofedCommitWarning />
      <TruncatedTreeBanner />
      <CodeViewContextBanners />
    </>
  )
}

function BlobBannersLegacy({blob, payload}: {blob: BlobPayload; payload: DataRouterFilePagePayload}) {
  return (
    <CurrentBlobProvider blob={blob}>
      <BlobBanners />
      <BlameBannersLegacy payload={payload} />
    </CurrentBlobProvider>
  )
}

export function BlobBanners() {
  return (
    <>
      <BlobTopBanners />
      <BlobMidBanners />
      <BlobLowerBanners />
      <CodeViewContextBanners />
    </>
  )
}

export function HeaderBanners() {
  return (
    <>
      <CodeViewContextBanners />
    </>
  )
}

function TruncatedTreeBanner() {
  const {items, totalCount} = useCurrentTree()
  const omittedCount = totalCount - items.length

  if (omittedCount <= 0) return null

  const omittedText = `${omittedCount.toLocaleString()} ${omittedCount === 1 ? 'entry was' : 'entries were'} omitted from the list.`

  return totalCount > 1000 ? (
    <Flash variant="warning" data-testid="repo-truncation-warning" className="tmp-mt-3">
      Sorry, we had to truncate this directory to {items.length.toLocaleString()} files. {omittedText} Latest commit
      info may be omitted.
    </Flash>
  ) : (
    <Flash variant="warning" data-testid="repo-truncation-warning" className="tmp-mt-3">
      {omittedText}
    </Flash>
  )
}

function CodeViewContextBanners() {
  const banners = useCodeViewBanners()
  let messageToAnnounce = ''
  for (const b of banners) {
    messageToAnnounce += b.message
  }
  forceAnnouncementToScreenReaders(messageToAnnounce)
  return (
    <>
      {banners.map((b, i) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <Flash key={i} variant={b.variant} className="tmp-mt-3">
          {b.message}
        </Flash>
      ))}
    </>
  )
}
